// Pure derive helpers for Today ritual flow (D20 Morning Planning, D21 Evening Shutdown).
// No side effects — all functions are deterministic given state + date string.
import { todayIso } from "../lib/date";
import type { LifeOsStateWithRecurring } from "../lib/life-os-contract";
import type { DailyPlan, DailyReview, Task } from "../types";

// ---------------------------------------------------------------------------
// Scoring constants for MIT suggestion ranking
// ---------------------------------------------------------------------------
const SCORE_OVERDUE = 3;
const SCORE_DUE_TODAY = 2;
const SCORE_PRIORITY: Record<string, number> = { high: 3, medium: 2, low: 1 };
const SCORE_LINKED = 1; // bonus when task has goalId or projectId

/** Compute ranking score for a single task relative to a reference date. */
function scoreMit(task: Task, date: string): number {
  let score = 0;
  // Guard empty dueDate: "" < date is true and would wrongly score as overdue.
  if (task.dueDate && task.dueDate < date) score += SCORE_OVERDUE;
  if (task.dueDate === date) score += SCORE_DUE_TODAY;
  score += SCORE_PRIORITY[task.priority] ?? 1;
  if (task.goalId || task.projectId) score += SCORE_LINKED;
  return score;
}

/**
 * Suggest Most Important Tasks for the given date.
 * Returns open (non-done) tasks ranked by score descending — up to top 7.
 * Used by MorningPlanningModal to present candidates.
 */
export function suggestMits(state: LifeOsStateWithRecurring, date = todayIso()): Task[] {
  return state.tasks
    .filter((t) => t.status !== "done")
    // D25: hide tasks deferred to a future date (not actionable yet).
    .filter((t) => !t.deferDate || t.deferDate <= date)
    .map((t) => ({ task: t, score: scoreMit(t, date) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 7)
    .map(({ task }) => task);
}

/** Find today's DailyPlan (id = date). Returns undefined if not planned yet. */
export function todaysPlan(state: LifeOsStateWithRecurring, date = todayIso()): DailyPlan | undefined {
  return state.dailyPlans.find((p) => p.id === date);
}

/** Find today's DailyReview (id = date). Returns undefined if not reviewed yet. */
export function todaysReview(state: LifeOsStateWithRecurring, date = todayIso()): DailyReview | undefined {
  return state.dailyReviews.find((r) => r.id === date);
}

/**
 * Resolve today's focus task list for the HERO tile.
 * Priority: if DailyPlan exists for today → return tasks in MIT order (filter deleted).
 * Fallback: suggestMits top 3 when no plan yet.
 */
export function todaysFocusTasks(state: LifeOsStateWithRecurring, date = todayIso()): Task[] {
  const plan = todaysPlan(state, date);
  if (plan && plan.mitTaskIds.length > 0) {
    // Preserve MIT ordering; filter tasks that may have been deleted
    const taskById = new Map(state.tasks.map((t) => [t.id, t]));
    return plan.mitTaskIds
      .map((id) => taskById.get(id))
      .filter((t): t is Task => t !== undefined);
  }
  // No plan yet — fallback to top 3 suggestions
  return suggestMits(state, date).slice(0, 3);
}

// ---------------------------------------------------------------------------
// Capacity derive helpers (D22 — used by MorningPlanningModal + Today lock)
// ---------------------------------------------------------------------------

/**
 * Sum estimateMinutes for the given MIT task IDs.
 * Missing tasks (deleted mid-day) contribute 0.
 */
export function plannedMinutes(mitTaskIds: string[], tasks: Task[]): number {
  const taskById = new Map(tasks.map((t) => [t.id, t]));
  return mitTaskIds.reduce((sum, id) => sum + (taskById.get(id)?.estimateMinutes ?? 0), 0);
}

/**
 * Returns true when the planned minutes exceed the given capacity.
 * Strict-greater: exactly at capacity is NOT over.
 */
export function isOverCapacity(plannedMin: number, capacityMin: number): boolean {
  return plannedMin > capacityMin;
}

/**
 * Collect tasks relevant for Evening Shutdown review.
 * Includes: tasks due today OR tasks listed in today's plan (MIT).
 * Deduplicates by id.
 */
export function todaysReviewTasks(state: LifeOsStateWithRecurring, date = todayIso()): Task[] {
  const plan = todaysPlan(state, date);
  const mitIds = new Set(plan?.mitTaskIds ?? []);
  const seen = new Set<string>();
  const result: Task[] = [];

  for (const t of state.tasks) {
    if (t.dueDate === date || mitIds.has(t.id)) {
      if (!seen.has(t.id)) {
        seen.add(t.id);
        result.push(t);
      }
    }
  }

  return result;
}
